<script setup lang="ts">
import { computed } from 'vue'
import UiIcon from '../../icons/Icon.vue'
import type { UiIconName } from '../../icons/definitions'
import UiSpinner from './Spinner.vue'

/**
 * `<UiIconButton>` — a square control whose only content is an icon.
 *
 * `label` is **required**, not optional. An icon-only button with no
 * accessible name is a button a screen-reader user hears as "button", and
 * this is the single most common accessibility defect in an admin UI. Making
 * the prop required means the failure is a type error rather than a finding in
 * an audit six months later. The label doubles as the tooltip text.
 */

const props = withDefaults(
  defineProps<{
    name: UiIconName
    /** Accessible name AND native tooltip. Required by design. */
    label: string
    variant?: 'ghost' | 'secondary' | 'primary' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    disabled?: boolean
    loading?: boolean
    /**
     * Toggle state. Leave undefined for a plain action button: Vue casts an
     * absent `boolean` prop to `false` unless a default is declared, and
     * `aria-pressed="false"` on an Edit button announces it as an unpressed
     * toggle. The explicit `undefined` default below is what keeps the
     * three-state distinction.
     */
    pressed?: boolean
    type?: 'button' | 'submit' | 'reset'
  }>(),
  { variant: 'ghost', size: 'md', type: 'button', pressed: undefined },
)

const emit = defineEmits<{ click: [MouseEvent] }>()

const isDisabled = computed(() => props.disabled || props.loading)

function onClick(event: MouseEvent) {
  if (isDisabled.value) return
  emit('click', event)
}
</script>

<template>
  <button
    class="ui-icon-button ui-focusable"
    :class="[`ui-icon-button--${variant}`, `ui-icon-button--${size}`, { 'is-pressed': pressed }]"
    :type="type"
    :disabled="isDisabled || undefined"
    :aria-label="label"
    :title="label"
    :aria-pressed="pressed === undefined ? undefined : pressed"
    :aria-busy="loading || undefined"
    @click="onClick"
  >
    <UiSpinner v-if="loading" size="1.1em" />
    <UiIcon v-else :name="name" size="1.15em" />
  </button>
</template>

<style scoped>
.ui-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition:
    background-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .ui-icon-button {
    transition: none;
  }
}

.ui-icon-button--sm {
  inline-size: 28px;
  block-size: 28px;
  font-size: var(--text-sm);
}

.ui-icon-button--md {
  inline-size: 36px;
  block-size: 36px;
  font-size: var(--text-base);
}

/* The portal's floor. Named from the token so it tracks the accessibility
   requirement rather than a number someone typed once. */
.ui-icon-button--lg {
  inline-size: var(--touch-target);
  block-size: var(--touch-target);
  font-size: var(--text-md);
}

.ui-icon-button--ghost:hover:not([disabled]) {
  background: var(--surface-muted);
  color: var(--text);
}

.ui-icon-button--secondary {
  background: var(--surface);
  border-color: var(--border-strong);
  color: var(--text);
}

.ui-icon-button--secondary:hover:not([disabled]) {
  background: var(--surface-muted);
}

.ui-icon-button--primary {
  background: var(--brand);
  border-color: var(--brand);
  color: var(--on-brand);
}

.ui-icon-button--primary:hover:not([disabled]) {
  background: var(--brand-hover);
  border-color: var(--brand-hover);
}

.ui-icon-button--danger {
  background: transparent;
  color: var(--danger);
}

.ui-icon-button--danger:hover:not([disabled]) {
  background: var(--danger-bg);
}

.ui-icon-button.is-pressed {
  background: var(--brand-subtle);
  color: var(--brand);
}

.ui-icon-button[disabled] {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
